import React, { useEffect, useState } from 'react';
import { toast } from 'src/utils/notification';
import request from 'src/request/request';
import { jwtEncrypt } from 'src/api/services/tools/service.jwtEncrypt';
import { run as runActions } from 'src/store/workspaces/workspaces/run';
import store from 'src/store/store';
import { AccountIMAPSettings, AccountEmail, AccountSMTPSettings, AccountType, Step, AccountCredentials } from './types';
import TokenDialog from './Account.dialog.token';
import SmtpDialog from './Account.dialog.smtp';
import ImapDialog from './Account.dialog.imap';
import TypeDialog from './Account.dialog.type';
import CredentialsDialog from './Account.dialog.credentials';

const AccountFlow: React.FC<{ navData: any }> = ({ navData }) => {

  const [step, setStep] = useState<Step>('type');
  const [accountType, setAccountType] = useState<AccountType>('email');
  const [showDialog, setShowDialog] = useState(true);
  const [loading, setLoading] = useState(false);
  const [name, setName] = useState<string>('');
  const [emailAddress, setEmailAddress] = useState<string>('');
  const [imap, setImap] = useState<AccountIMAPSettings>({ host: '', port: 993, secure: 'ssl', auth_method: 'password', username: '', password: ''});
  const [smtp, setSmtp] = useState<AccountSMTPSettings>({ host: '', port: 465, secure: 'ssl', auth_method: 'password', username: '', password: '', require_tls: 'true'});
  const [tokenApi, setTokenApi] = useState<string>('');   
  const [tokenValue, setTokenValue] = useState<string>(''); 
  const [credentialsValue, setCredentialsValue] = useState<string>(''); 

  const event = navData?.widgetData?.events?.find((e: any) => e.type === 'onSubmit');
  
  if (!event) {
    toast.warning('Kein Event Key');
    return null;
  }


  const reset = async () => {
    setStep('');
    setAccountType('email');
    setShowDialog(true);
    setLoading(false);
    setName('');
    setEmailAddress('');
    setTokenApi('');
    setTokenValue('');
    setCredentialsValue('');
    setImap({ host: '', port: 993, secure: 'ssl', auth_method: 'password', username: '', password: ''});
    setSmtp({ host: '', port: 465, secure: 'ssl', auth_method: 'password', username: '', password: '', require_tls: 'true'});
  };

  useEffect(() => { return () => { (async () => { await reset();})();};}, []);

  const buildPayload = (): AccountEmail | AccountCredentials | Record<string, never> => {
    switch (accountType) {
      case 'email':
        return {
          imap,
          smtp,
          type: accountType,
          email_address: emailAddress,
        } as AccountEmail;

      case 'credentials':
        return {
          value: credentialsValue,
          type: accountType,
        } as AccountCredentials;

      default:
        return {};
    }
  };


  const createAccount = async (jwtToken: any) => {
    const res = await request(true, 'POST', 'api/v1/accounts', { jwtToken });
    if (res?.success) {
      await runActions(event.key, null, { account: { ...res.account, name, type: accountType }, channel: { name, key: emailAddress, type: accountType },});
      toast.success('Account erfolgreich erstellt');
      setStep('done');
      setShowDialog(false);
    } else {
      toast.warning(res?.message || 'Fehler beim Anlegen');
    }
  };

  const handleSubmit = async () => {
    try {
      setLoading(true);
      const { data } = await jwtEncrypt(buildPayload());

      if (accountType === 'email') {
        if (step === 'imap') {
          if (!name || !emailAddress || !imap.host || !imap.port || !imap.secure || !imap.auth_method || !imap.username || !imap.password) {
            toast.warning('Bitte IMAP-Felder vollständig ausfüllen.'); return;
          }

          const res = await request(true, 'POST', 'api/v1/accounts/verify', { jwtToken: data });
          
          if (res?.message) toast.info(res.message);
          if(res.success) setStep('smtp');

          setSmtp(prev => ({ ...prev,
            host: imap.host,
            port: imap.port === 993 ? 465 : prev.port,
            secure: imap.secure,
            auth_method: imap.auth_method,
            username: imap.username,
            password: imap.password,
          }));
        }

        if (step === 'smtp') {
          if (!smtp.host || !smtp.port || !smtp.secure || !smtp.auth_method || !smtp.username || !smtp.password || !smtp.require_tls) {
            toast.warning('Bitte SMTP-Felder vollständig ausfüllen.'); return;
          }
          const res = await request(true, 'POST', 'api/v1/accounts/verify', { jwtToken: data });

          if (res?.message) toast.info(res.message);
          if (res?.success) { setTokenApi(res.token); setStep('token'); }
        }

        if (step === 'token') {
          if (tokenValue !== tokenApi) { toast.warning('Token stimmt nicht überein'); return; }
          await createAccount(data);
        }
      } else if (accountType === 'credentials' && step === 'credentials') {
        if (!name || !credentialsValue) { toast.warning('Bitte Name und Credentials angeben.'); return; }
        await createAccount(data);
      }
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };


  const onClose = async () => {
    await reset();
    const currentBreadcrumb = store.getState().workspaces.instances.find((i: any) => i.isActive)?.breadcrumb?.currentPage;
    await runActions(currentBreadcrumb?.key, currentBreadcrumb?.id, currentBreadcrumb ?? {});
  };

  return (
    <>
      {step === 'type' && (
        <TypeDialog
          visible={showDialog}
          onClose={onClose}
          accountType={accountType}
          onChangeType={setAccountType}
          onNext={() => setStep(accountType === 'email' ? 'imap' : 'credentials')}
        />
      )}

      {step === 'imap' && (
        <ImapDialog
          visible={showDialog}
          onClose={onClose}
          loading={loading}
          name={name}
          emailAddress={emailAddress}
          imap={imap}
          setName={setName}
          setEmailAddress={setEmailAddress}
          setImap={setImap}
          onNext={handleSubmit}
        />
      )}

      {step === 'smtp' && (
        <SmtpDialog
          visible={showDialog}
          onClose={onClose}
          loading={loading}
          smtp={smtp}
          setSmtp={setSmtp}
          onVerify={handleSubmit}
        />
      )}

      {step === 'token' && (
        <TokenDialog
          visible={showDialog}
          onClose={onClose}
          tokenValue={tokenValue}
          setTokenValue={setTokenValue}
          onFinish={handleSubmit}
        />
      )}

      {step === 'credentials' && (
        <CredentialsDialog
          visible={showDialog}
          onClose={onClose}
          loading={loading}
          name={name}
          credentialsValue={credentialsValue}
          setName={setName}
          setCredentialsValue={setCredentialsValue}
          onSave={handleSubmit}
        />
      )}
    </>
  );
};

export { AccountFlow };
export default AccountFlow;
